// ═══════════════════════════════════════════════════════════════════════════
// Supabase Storage image sizing.
//
// Avatars and show covers are stored at whatever resolution the user uploaded
// — often 4K phone photos — and were being sent to the browser full-size and
// downscaled in CSS (e.g. a 3840×2160 jpg painted into a 32px avatar). That's
// megabytes per image for a thumbnail.
//
// Supabase Storage can transform on the fly: swapping `/object/public/` for
// `/render/image/public/` and adding width/height/quality serves a right-sized
// image from the CDN. Verified available on this project's plan.
//
// `storageImage()` is a safe no-op for anything that isn't a public Supabase
// Storage URL (OAuth avatars, gravatars, data URIs, already-transformed URLs),
// so it can be wrapped around any image src without special-casing the source.
// ═══════════════════════════════════════════════════════════════════════════

const PUBLIC_OBJECT = '/storage/v1/object/public/';
const RENDER_IMAGE = '/storage/v1/render/image/public/';

/**
 * @param {string} url            an image URL (any source)
 * @param {object} opts
 * @param {number} [opts.width]   target width in CSS px (multiply by DPR at call site if you want crispness)
 * @param {number} [opts.height]  target height in CSS px
 * @param {number} [opts.quality] 20–100, default 70
 * @param {'cover'|'contain'|'fill'} [opts.resize] override the fit; see note below
 * @returns {string} a resize URL for Supabase storage images; the original URL otherwise
 *
 * Note on `resize`: Supabase's `cover` REQUIRES both width and height — given
 * only one, it pins that axis and leaves the other at the original size (a
 * distorted, oversized result). So when a single dimension is passed we default
 * to `contain`, which scales proportionally; with both we default to `cover`
 * (an exact crop, ideal for square avatars). Callers that pass one dimension
 * for a box that uses CSS `object-fit: cover` still look right — the proportional
 * image is cropped by the browser.
 */
export function storageImage(url, { width, height, quality = 70, resize } = {}) {
  if (!url || typeof url !== 'string') return url;
  // Only rewrite un-transformed public Supabase Storage objects.
  if (!url.includes(PUBLIC_OBJECT)) return url;
  if (url.includes('/render/image/')) return url; // already transformed
  if (!width && !height) return url;

  const fit = resize || (width && height ? 'cover' : 'contain');
  const base = url.replace(PUBLIC_OBJECT, RENDER_IMAGE);
  const params = [
    width && `width=${Math.round(width)}`,
    height && `height=${Math.round(height)}`,
    `quality=${quality}`,
    `resize=${fit}`,
  ].filter(Boolean).join('&');
  return `${base}${base.includes('?') ? '&' : '?'}${params}`;
}

/** Device-pixel-aware width: asks for a sharper image on retina, capped at 3×. */
export function dprWidth(cssPx) {
  const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
  return Math.round(cssPx * Math.min(dpr, 3));
}

/**
 * onError handler for an <img src={storageImage(url, opts)}>. Supabase's
 * Storage Image Transformations endpoint (the /render/image/public/ URL
 * storageImage() builds) is a paid-plan feature — on a project without it
 * enabled, every transformed image request 400s and the <img> renders
 * broken. Falls back once to the original, untransformed `rawUrl` so the
 * photo still shows (full-size instead of resized) rather than a broken
 * image icon. Usage: <img src={storageImage(url, opts)} onError={(e) =>
 * onStorageImageError(e, url)} />
 */
export function onStorageImageError(e, rawUrl) {
  const img = e?.currentTarget;
  if (!img || !rawUrl || img.src === rawUrl) return;
  img.src = rawUrl;
}
